import { useState, useCallback, useEffect } from 'react';
import { getFilterList, deleteFilter } from '@opm/services';
import { getPagePath, PAGE_ENUM_MAP } from '../utils';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';

/**
 * 筛选器数据项类型
 */
export interface IFilterItem {
  filter_id: number;
  filter_name: string;
  filter_cond: string;
  system?: string;
}

/**
 * useHistoryFilterQuery Hook 参数
 */
export interface IUseHistoryFilterQueryProps {
  /** 最大保存数量，默认50 */
  maxSaveCount?: number;
  /** 页面路由，确定请求什么页面的过滤器 */
  pagePath?: PAGE_ENUM_MAP;
  /** 是否自动请求，默认 true */
  autoFetch?: boolean;
}

/**
 * useHistoryFilterQuery Hook 返回值
 */
export interface IUseHistoryFilterQueryReturn {
  /** 筛选器列表 */
  filterList: IFilterItem[];
  /** 加载状态 */
  loading: boolean;
  /** 获取筛选器列表 */
  fetchFilterList: (topN?: number) => Promise<void>;
  /** 删除筛选器 */
  deleteFilterItem: (filterId: number) => Promise<boolean>;
  /** 向列表前面添加筛选器 */
  prependFilterItem: (item: IFilterItem) => void;
  /** 设置筛选器列表 */
  setFilterList: React.Dispatch<React.SetStateAction<IFilterItem[]>>;
}

/**
 * 历史筛选器查询和删除 Hook
 * 封装筛选器列表的获取、删除操作
 *
 * @param props - 配置参数
 * @returns Hook 返回值
 *
 * @example
 * ```tsx
 * // 默认自动请求
 * const {
 *   filterList,
 *   loading,
 *   fetchFilterList,
 *   deleteFilterItem,
 *   prependFilterItem
 * } = useHistoryFilterQuery({ maxSaveCount: 50, pagePath: '/marketing' });
 *
 * // 禁用自动请求，手动控制
 * const {
 *   filterList,
 *   fetchFilterList
 * } = useHistoryFilterQuery({ maxSaveCount: 50, autoFetch: false });
 *
 * // 手动获取列表
 * await fetchFilterList();
 *
 * // 删除筛选器
 * await deleteFilterItem(123);
 *
 * // 向列表前面添加新筛选器
 * prependFilterItem({
 *   filter_id: 123,
 *   filter_name: '新筛选器',
 *   filter_info: '{}'
 * });
 * ```
 */
export function useHistoryFilterQuery(
  props: IUseHistoryFilterQueryProps,
): IUseHistoryFilterQueryReturn {
  const { maxSaveCount = 50, pagePath, autoFetch = true } = props || {};
  const { loading, wrapperCancelRequest } =
    useCancelRequestHook<IFilterItem[]>();
  const [filterList, setFilterList] = useState<IFilterItem[]>([]);

  /**
   * 自动请求筛选器列表
   */
  useEffect(() => {
    if (autoFetch) {
      fetchFilterList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch]);

  /**
   * 获取筛选器列表
   */
  const fetchFilterList = useCallback(async (topN?: number) => {
    try {
      const data = await wrapperCancelRequest(getFilterList, {
        top_n: topN ?? maxSaveCount,
        page_path: getPagePath(pagePath),
        business_type: 1,
      });
      setFilterList(data || []);
    } catch (error) {
      console.error('Failed to fetch filter list:', error);
      setFilterList([]);
    }
  }, []);

  /**
   * 删除筛选器
   */
  const deleteFilterItem = useCallback(
    async (filterId: number): Promise<boolean> => {
      try {
        await wrapperCancelRequest(deleteFilter, {
          filter_id: filterId,
          business_type: 1,
        });
        // 删除成功后更新列表
        setFilterList(prev => prev.filter(item => item.filter_id !== filterId));
        return true;
      } catch (error) {
        console.error('Failed to delete filter:', error);
        return false;
      }
    },
    [],
  );

  /**
   * 向列表前面添加筛选器
   */
  const prependFilterItem = useCallback((item: IFilterItem) => {
    setFilterList(prev => [item, ...prev]);
  }, []);

  return {
    filterList,
    loading,
    fetchFilterList,
    deleteFilterItem,
    prependFilterItem,
    setFilterList,
  };
}
